import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import { reasonText } from '../lib/reasons';
import { useAtRisk } from '../queries';

/**
 * Class-detail "Needs attention" panel (staff). Every flagged pupil arrives
 * with `reasons: [{code, value, threshold}]` so the teacher sees WHY — the
 * codes are translated here, never rendered raw.
 */
export function AtRiskPanel({ classPublicId }: { classPublicId: string }) {
  const { t } = useTranslation(['progress', 'common']);
  const query = useAtRisk(classPublicId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <AlertTriangle className="text-muted-foreground size-4" aria-hidden />
          {t('progress:atRisk.title')}
        </CardTitle>
        <CardDescription>{t('progress:atRisk.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <Skeleton className="h-16 w-full" aria-hidden />
        ) : query.isError ? (
          <p className="text-destructive text-sm">
            {(query.error as unknown as ApiError)?.message ?? t('progress:atRisk.loadFailed')}
          </p>
        ) : (query.data?.pupils.length ?? 0) === 0 ? (
          <p className="text-muted-foreground text-sm">{t('progress:atRisk.empty')}</p>
        ) : (
          <ul className="divide-y">
            {query.data?.pupils.map(row => (
              <li key={row.pupil.public_id} className="flex flex-wrap items-start gap-3 py-2.5">
                <span className="min-w-40 text-sm font-medium">{row.pupil.name}</span>
                <ul className="text-muted-foreground flex-1 space-y-0.5 text-xs">
                  {row.reasons.map(reason => (
                    <li key={reason.code}>{reasonText(reason, t)}</li>
                  ))}
                </ul>
                <Button variant="outline" size="sm" asChild>
                  <Link to={`/progress/pupils/${row.pupil.public_id}`}>
                    {t('progress:atRisk.viewProgress')}
                  </Link>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
